import './errorBoundary.scss'

import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Typography from '@mui/material/Typography'
import React from 'react'
import {
  SEVERITY_LEVEL_ERROR,
  trackException,
  trackTrace,
} from 'src/hooks/useAppInsights'

import circleSvg from '../../assets/images/caution.svg'

type ErrorBoundaryProps = {
  children?: React.ReactNode
}

type ErrorBoundaryState = {
  hasError: boolean
}

export const CHUNK_ERROR_RELOADED_AT_KEY = 'chunkErrorReloadedAt'
export const CHUNK_ERROR_RELOAD_WINDOW_MS = 60000

const CHUNK_LOAD_ERROR_NAME = 'ChunkLoadError'
const CHUNK_LOAD_ERROR_MESSAGE_PATTERN = /loading (?:css )?chunk .+ failed/i

export const isChunkLoadError = (error: Error): boolean =>
  error.name === CHUNK_LOAD_ERROR_NAME ||
  CHUNK_LOAD_ERROR_MESSAGE_PATTERN.test(error.message)

export class ErrorBoundary extends React.Component<
  ErrorBoundaryProps,
  ErrorBoundaryState
> {
  constructor(props: ErrorBoundaryProps) {
    super(props)
    this.state = { hasError: false }
  }

  static getDerivedStateFromError(): ErrorBoundaryState {
    // update state so the next render will show the fallback UI
    return { hasError: true }
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo): void {
    if (isChunkLoadError(error) && this.reloadForNewDeployment()) {
      trackTrace({
        message: `Static Web App: reloading after chunk load failure: ${error}`,
        severityLevel: SEVERITY_LEVEL_ERROR,
      })
      return
    }
    trackTrace({
      message: `Static Web App: ${error}, ErrorInfo: ${JSON.stringify(
        errorInfo
      )}`,
      severityLevel: SEVERITY_LEVEL_ERROR,
    })
    trackException({
      exception: error,
      severityLevel: SEVERITY_LEVEL_ERROR,
    })
  }

  // A ChunkLoadError in production usually means a new deployment replaced
  // the hashed chunk files this session's bundle references; reloading fetches
  // the new index.html and chunk manifest. The timestamp guard stops a reload
  // loop when the chunk is still missing after a fresh load.
  reloadForNewDeployment = (): boolean => {
    const lastReloadedAt = Number(
      sessionStorage.getItem(CHUNK_ERROR_RELOADED_AT_KEY)
    )
    const now = Date.now()
    if (lastReloadedAt && now - lastReloadedAt < CHUNK_ERROR_RELOAD_WINDOW_MS) {
      return false
    }
    sessionStorage.setItem(CHUNK_ERROR_RELOADED_AT_KEY, String(now))
    window.location.reload()
    return true
  }

  handleBackHome = () => {
    window.location.href = '/'
  }

  render(): React.ReactNode {
    if (this.state.hasError) {
      return (
        <Box className="error-boundary">
          <Box className="center-container">
            <Box className="svg-container">
              <Box
                component="img"
                src={circleSvg}
                className="circle-svg"
                alt="Warning icon"
              />
            </Box>
            <Typography variant="h4" className="header-text">
              Oops! Something went wrong
            </Typography>
            <Typography className="body-text">
              Please try again in some time.
            </Typography>
            <Box className="button-layout">
              <Button
                variant="outlined"
                color="primary"
                className="button-style"
                onClick={this.handleBackHome}
              >
                <Box component={'span'} className="button-body">
                  Go to Home Page
                </Box>
              </Button>
            </Box>
          </Box>
        </Box>
      )
    }
    return this.props.children
  }
}
